// Polynomial stamina TREND, mirroring the bike StaminaChart's approach: fit the
// best (by R²) polynomial of degree 1..maxDegree to the per-chunk stamina scores
// and return the smooth fitted curve — much more indicative of the fatigue
// trajectory than the noisy raw line.
//
// x is normalized to [0,1] before fitting so a high-degree Vandermonde stays
// numerically stable even over thousands of chunks (raw indices would overflow).

const transpose = (m) => m[0].map((_, i) => m.map((row) => row[i]));
const matMul = (a, b) =>
  a.map((row) => b[0].map((_, j) => row.reduce((s, v, k) => s + v * b[k][j], 0)));
const matVec = (m, v) => m.map((row) => row.reduce((s, val, i) => s + val * v[i], 0));

function gaussianSolve(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(M[k][i]) > Math.abs(M[maxRow][i])) maxRow = k;
    }
    [M[i], M[maxRow]] = [M[maxRow], M[i]];
    if (Math.abs(M[i][i]) < 1e-12) return null; // singular
    for (let k = i + 1; k < n; k++) {
      const f = M[k][i] / M[i][i];
      for (let j = i; j <= n; j++) M[k][j] -= f * M[i][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n];
    for (let j = i + 1; j < n; j++) x[i] -= M[i][j] * x[j];
    x[i] /= M[i][i];
  }
  return x;
}

function fitPolynomial(xNorm, y, degree) {
  const n = xNorm.length;
  const X = xNorm.map((xi) => Array.from({ length: degree + 1 }, (_, j) => xi ** j));
  const XT = transpose(X);
  const coef = gaussianSolve(matMul(XT, X), matVec(XT, y));
  if (!coef) return null;
  const predicted = xNorm.map((xi) => coef.reduce((s, c, j) => s + c * xi ** j, 0));
  const yMean = y.reduce((s, v) => s + v, 0) / n;
  const ssTot = y.reduce((s, v) => s + (v - yMean) ** 2, 0);
  const ssRes = y.reduce((s, v, i) => s + (v - predicted[i]) ** 2, 0);
  return { predicted, r2: ssTot > 0 ? 1 - ssRes / ssTot : 0 };
}

/**
 * @param {Array<number|null>} values - per-chunk stamina scores (nulls allowed)
 * @returns {Array<number|null>} smooth trend aligned to `values` (null where input was null)
 */
export function computeStaminaTrend(values, { maxDegree = 6, clampMin = 0, clampMax = 150 } = {}) {
  const out = values.map(() => null);
  const idx = [];
  const y = [];
  values.forEach((v, i) => {
    if (v != null && !Number.isNaN(v)) { idx.push(i); y.push(v); }
  });
  if (y.length < 3) {
    idx.forEach((origI, k) => { out[origI] = y[k]; }); // too few points → passthrough
    return out;
  }
  const xNorm = idx.map((_, k) => k / (idx.length - 1));
  let best = null;
  let bestR = -Infinity;
  for (let d = 1; d <= Math.min(maxDegree, y.length - 1); d++) {
    const r = fitPolynomial(xNorm, y, d);
    if (r && r.r2 > bestR) { bestR = r.r2; best = r; }
  }
  if (!best) {
    idx.forEach((origI, k) => { out[origI] = y[k]; });
    return out;
  }
  idx.forEach((origI, k) => {
    out[origI] = Math.max(clampMin, Math.min(clampMax, best.predicted[k]));
  });
  return out;
}
